import { type Directive, type WatchStopHandle, watchEffect } from 'vue'
import { type FeatureFlag, useFeatures } from '@/composables/useFeatures'

// v-feature — conditionally hide an element when a feature flag is
// off for the current tenant. Used like:
//
//   <button v-feature="'notes.sharing'">Del notat</button>
//
// or with an inverse "show when off":
//
//   <p v-feature.not="'notes.sharing'">...legg til en kollega...</p>
//
// Why a directive, not v-if + useFeatures in every component:
// feature gating is a cross-cutting concern, same family as
// v-focus-trap. Inlining `useFeatures()` in dozens of components
// adds noise; the directive keeps the call site to a single
// declarative attribute.
//
// Each element gets its own watchEffect over the flags store, so the
// element tracks the store itself: flags that land after the host
// mounted (App.vue fires loadFeatures() without awaiting it) or change
// on a refresh() apply without the host component re-rendering. The
// pre-watch version snapshotted peek() in mounted/updated only — the
// dashboard search trigger froze at whatever the store said the last
// time the shell happened to re-render (a locale switch re-renders
// every $t binding), and only a hard reload recovered it.
//
// Caveat: this hides via display:none — the element stays mounted.
// Don't use it for layout containers where the empty slot matters;
// use a `v-if` + a composable reference in those cases.

type Binding = FeatureFlag

const watchers = new WeakMap<HTMLElement, { key: string; stop: WatchStopHandle }>()

function bindingKey(flag: FeatureFlag, invert: boolean): string {
  return invert ? `!${flag}` : flag
}

function apply(el: HTMLElement, shouldShow: boolean) {
  if (shouldShow) {
    el.style.removeProperty('display')
    el.removeAttribute('aria-hidden')
  } else {
    el.style.display = 'none'
    el.setAttribute('aria-hidden', 'true')
  }
}

function track(el: HTMLElement, flag: FeatureFlag, invert: boolean) {
  const { flags } = useFeatures()
  const stop = watchEffect(() => {
    const on = flags.value[flag] === true
    apply(el, invert ? !on : on)
  })
  watchers.set(el, { key: bindingKey(flag, invert), stop })
}

export const vFeature: Directive<HTMLElement, Binding> = {
  mounted(el, binding) {
    track(el, binding.value, binding.modifiers.not === true)
  },
  updated(el, binding) {
    // Re-track only when the bound flag actually changed — updated fires
    // on every host re-render, and the standing watcher already covers
    // store changes.
    const invert = binding.modifiers.not === true
    const prev = watchers.get(el)
    if (prev?.key === bindingKey(binding.value, invert)) return
    prev?.stop()
    track(el, binding.value, invert)
  },
  unmounted(el) {
    watchers.get(el)?.stop()
    watchers.delete(el)
  },
}
